import React, {useEffect, useState} from "react";
import {RootState} from "../../../../store/store";
import {setIsLoading} from "../../../../store/actions/dateRange.actions";
import {connect, ConnectedProps} from "react-redux";
import {IDateRangeGState, ILoadingComponents} from "../../../../interfaces/datesGlobalState.interface"
import {useParams} from "react-router-dom";
import {useAppDispatch, useRefreshInterval} from "../../../../store/hooks";
import {adServersBaseUrl, ComparisonState} from "../../../../config/generals";
import {AlertTypes, apiClient, Grid, IAlertData, setAlert, Paper, Typography, Skeleton, useTheme} from "@leap4/frontend";
import {getDefaultTimeDisplay, indexToTimeInterval} from "../../common/charts/charts";
import AdserverTagsTable from "./TagsTable/AdserverTagsTable";
import ComparisonOverviewGraph, { IOverviewGraphResponse } from "../../common/ComparisonGraph";
import {OverviewGeneralInfo} from "../../common/OverviewGeneralInfo/OverviewGeneralInfo";
import {ReportHeaderWithProps} from "./ReportHeader/AdServerReportHeader";
import {MetricsComparison} from "../../../shared/MetricsComparison/MetricsComparison.container";
import {IAdserverOverview} from "./interfaces";
import {metricsComparisonKeyMap} from "../../../shared/MetricsComparison/MetricsComparisonHelpers";
import {DataPointCards} from "../../home/components/dataPointsCards/dataPointsCards";
import {PredictiveGraphContainer} from "../../common/PredictiveGraph/PredictiveGraph.container";
import {ITopTagsRangeCombo} from "../../home/interfaces";
import HomeTopTagsTable from "../../home/components/topCombosTable/HomeTopTagsTable";
import {LastUpdatedAt} from "../../common/LastUpdatedAt/LastUpdatedAt";
import moment from "moment";
import {checkIfDateIsInComparisonOptions, getComparisonOptionsDates} from "../../common/time.utils";
import {Server} from "@leap4/auth";
import {OneOrgOnly} from "../../../shared/OneOrgOnly/OneOrgOnly";

const mapStateToProps = (state: RootState) => ({
    dates: state.dates,
    selectedOrganization:state.auth.selectedOrganizationId
});

const mapActions = {
    setIsLoading,
    setAlert
}

const connector = connect(mapStateToProps, mapActions);

type PropsFromRedux = ConnectedProps<typeof connector>;

export interface IPropsAdserverOverview extends PropsFromRedux {
    dates: IDateRangeGState
    setIsLoading: (isLoading: Partial<ILoadingComponents>) => void
    setAlert: (alert: IAlertData) => void
}

export interface IAsServerOverviewResult{
    overview: IAdserverOverview,
    comboTags: ITopTagsRangeCombo,
    lastUpdatedAt: Date
}

const AdServerOverview: React.FC<IPropsAdserverOverview> = ({
                                                                dates: {
                                                                    startDate,
                                                                    endDate,
                                                                    isLoading,
                                                                    selectedComparison
                                                                },
                                                                setIsLoading,
                                                                setAlert,
                                                                selectedOrganization
                                                            }): JSX.Element => {
    // Component State
    const theme = useTheme()
    const [data, setData] = useState<IAsServerOverviewResult>(null);
    const [graphData, setGraphData] = useState<IOverviewGraphResponse>(null)
    const [updatedAt, setUpdatedAt] = useState<Date>(null)
    const [graphInterval, setGraphInterval] = useState<number>(getDefaultTimeDisplay(startDate, endDate))
    const [datapointsForCards, setDatapointsForCards] = useState([])
    const [specificComparisonDate, setSpecificComparisonDate] = useState()
    const [optionalComparisonDates] = useState<any>(getComparisonOptionsDates(startDate, endDate))
    const [comparisonData, setComparisonData] = useState<ComparisonState>({
        compare: selectedComparison,
        data: null
    })
    useRefreshInterval(() => {
        if (startDate === endDate && startDate === moment().format("YYYY-MM-DD")) {
            fetchGeneralData()
            fetchGraphData()
        }
    })

    useEffect(() => {
        if (specificComparisonDate && !checkIfDateIsInComparisonOptions(specificComparisonDate, optionalComparisonDates)) {
            fetchGeneralData()
            fetchGraphData()
        }
    }, [specificComparisonDate])



    const params = useParams<{ id: string }>()
    const dispatch = useAppDispatch();

    // Component Computed Properties
    const adServerName = data?.overview?.adServer.name

    // Component Methods
    const fetchGeneralData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGeneral: true});

                const baseUrl = `${adServersBaseUrl}/overview/${params.id}`;
                const apiResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null
                })

                const apiData: IAsServerOverviewResult = apiResponse.data
                setData(apiData)
                if(apiData.lastUpdatedAt){
                    setUpdatedAt(new Date(apiData.lastUpdatedAt))
                }
            } catch (err) {
                setAlert({msg: "Couldnt fetch data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGeneral: false});
            }
        })()
    };
    const fetchGraphData = () => {
        (async () => {
            try {
                setIsLoading({isLoadingGraph: true});
                const baseUrl = `${adServersBaseUrl}/overview/${params.id}/graph`
                const graphResponse = await apiClient.post(baseUrl, {
                    startDate,
                    endDate,
                    specificComparisonDate: specificComparisonDate || null,
                    timeInterval: indexToTimeInterval(graphInterval)
                })
                const currentGraphData: IOverviewGraphResponse = graphResponse.data
                setGraphData(currentGraphData)
            } catch (err) {
                setAlert({msg: "Could'nt fetch Graph Data", type: AlertTypes.FAILURE, d: dispatch})
            } finally {
                setIsLoading({isLoadingGraph: false});
            }
        })()
    }
    const updateTimeInterval = () => {
        const newInterval = getDefaultTimeDisplay(startDate, endDate);
        if (newInterval !== graphInterval) {
            setGraphInterval(getDefaultTimeDisplay(startDate, endDate))
        } else {
            fetchGraphData()
        }
    }
    const updateComparisonData = () => {
        const dataForComparison = data?.overview
        let comparisonData;
        switch (selectedComparison) {
            case 1:
                comparisonData = dataForComparison?.dayAgo.totals
                break;
            case 7:
                comparisonData = dataForComparison?.weekAgo.totals
                break;
            case 28:
                comparisonData = dataForComparison?.monthAgo.totals
                break
            case 30:
                comparisonData = dataForComparison?.fullMonthAgo?.totals
                break
            case -1:
                comparisonData = dataForComparison?.specificComparisonDate?.totals
                break;
            default:
                comparisonData = dataForComparison?.selectedDates.totals
                break;
        }
        setComparisonData({compare: selectedComparison, data: comparisonData})
    }

    const getDatapointsCardsData = (newSelectedComparison) =>{
        const datapointsCardsData = data?.overview?.selectedDates.datapoints.map(dp=>{
            const compareData = data?.overview[metricsComparisonKeyMap[newSelectedComparison]]?.datapoints.find(dap => dap._id === dp.dataPoint._id )?.totals
            return {...dp.dataPoint, adServer:data.overview.adServer, totals: dp.totals, compareData}
        })
        setDatapointsForCards(datapointsCardsData)
    }

    // Component Lifecycle && Dependencies
    useEffect(updateTimeInterval, [startDate, endDate,selectedOrganization])
    useEffect(fetchGeneralData, [startDate, endDate, params.id,selectedOrganization])
    useEffect(fetchGraphData, [params.id, graphInterval,selectedOrganization]);
    useEffect(updateComparisonData, [data, selectedComparison,selectedOrganization]);
    useEffect(() =>{
        getDatapointsCardsData(selectedComparison)
    }, [selectedComparison, data])


    const tagsAndTheirMetrics = data?.overview?.selectedDates.datapoints.reduce((accumulator, currDatapoint) => {
        const {tags} = currDatapoint
        tags.forEach((tag) => {
            const {tag: currTag, totals} = tag;
            accumulator.push({tag:currTag,totals})
        })
        return accumulator
    }, [])
    const compareData =data && data.overview[metricsComparisonKeyMap[comparisonData.compare]]?.datapoints?.reduce((acc,currVal)=>{
        currVal.tags.forEach(currTag=>acc.push({totals:currTag.totals,tag:currTag.tag,_id:currTag._id}))
        return acc;
    },[])


    if (!isLoading.isLoadingGeneral && !data) return (<><ReportHeaderWithProps entityName={adServerName} entityId={params.id}/> <Typography
        variant={"overline"}> Error
        Getting Adserver Data</Typography></>)

    const lastChosenOrganization = localStorage.getItem(Server.headers.X_ORGANZIZATION).split('-')
    if (lastChosenOrganization.length > 1) {
        return <OneOrgOnly />
    }


    return (
        <>
            <ReportHeaderWithProps entityName={adServerName} entityId={params.id}/>
            <Grid container justifyContent={"flex-start"} alignItems={"stretch"}
                  style={{marginRight: "15px", filter: theme.palette.mode === 'dark'? 'drop-shadow(0px 0px 2px lightgray' : 'drop-shadow(0px 0px 5px lightgray'}}
                  spacing={2} width={"100%"}>
                <>
                    <Grid size={{ xs: 12, lg: 4 }} sx={{ flexShrink: 0, paddingLeft: { xs: 0, lg: '12px' } }}>
                        <Grid container direction={"column"} spacing={1} sx={{ height: '100%' }}>
                            <Grid size={12} height={"100%"} display={'flex'} flexDirection={'column'} gap={2} sx={{ minHeight: 0 }}>
                                <Grid size={12} sx={{ flex: 1, minHeight: 0 }}>
                                    <MetricsComparison optionalComparisonDates={optionalComparisonDates} specificComparisonDate={specificComparisonDate}
                                                       setSpecificComparisonDate={setSpecificComparisonDate} />
                                </Grid>
                                {updatedAt && (
                                    <Grid size={12} sx={{ flex: 0 }}>
                                        <LastUpdatedAt updatedAt={updatedAt} />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 8 }} sx={{ flexShrink: 1, minWidth: 0, paddingRight: { xs: 0, lg: '12px' } }}>
                        {!isLoading.isLoadingGeneral && data ?
                            <Paper elevation={5}
                                   style={{
                                       padding: "10px 0",
                                       height: '100%',
                                       margin: '0 0 0 10px',
                                       display: 'flex',
                                       alignItems: 'center',
                                       borderRadius: "16px",
                                       boxShadow: 'none'
                                   }}>
                                <Grid container height={"100%"} justifyContent={"center"} sx={{ width: '100%' }}>
                                    <Grid item height={"100%"} width={"100%"}>
                                        <OverviewGeneralInfo pageResult={data.overview}
                                                             comparisonData={comparisonData}

                                        />
                                    </Grid>
                                </Grid>
                            </Paper> :
                            <Skeleton  sx={{borderRadius: "16px"}} variant={"rounded"} height={"150px"} width={"100%"} animation={"wave"}/>}
                    </Grid>
                    <Grid item sx={{height: '400px', margin: '0 auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <ComparisonOverviewGraph isLoading={isLoading.isLoadingGraph} graphData={graphData}
                                                 startDate={startDate}
                                                 endDate={endDate}
                                                 setNewInterval={setGraphInterval} graphIntervalIndx={graphInterval}
                        />

                    </Grid>
                    <Grid item xs={12} style={{margin: '0 10px'}}>
                        {!isLoading.isLoadingGeneral && data ?
                            <AdserverTagsTable loading={isLoading.isLoadingGeneral} refreshFunc={fetchGeneralData}
                                               metrics={tagsAndTheirMetrics}
                                               compareData={compareData}
                            /> :
                            <Skeleton sx={{borderRadius: "16px"}} variant={"rounded"} animation={"wave"} height={"600px"} />}
                    </Grid>
                    {isLoading.isLoadingGeneral ?
                        <Skeleton animation={"wave"} sx={{borderRadius: "16px", m:5}} variant={"rounded"} height={"600px"}/> :
                        data.comboTags.selectedDates.length > 0?
                        <Grid item xs={12} sx={{margin: '0 10px'}}>
                            <HomeTopTagsTable tags={data.comboTags.selectedDates || []}
                                              isLoading={isLoading.isLoadingGeneral}
                                              compareData={data.comboTags[metricsComparisonKeyMap[comparisonData.compare]]}


                            />
                        </Grid>: <Grid></Grid>}
                    <Grid item xs={12} style={{margin: '0 10px'}}>
                        <DataPointCards dataPoints={datapointsForCards} isLoading={isLoading.isLoadingGeneral}/>
                    </Grid>
                    <Grid item sm={12} sx={{height: '400px', margin: '10px auto', width: {xs: '95%', sm: '96%', md: '99%'}}}>
                        <PredictiveGraphContainer url={`${adServersBaseUrl}/overview/${params.id}/predictive-graph`} bodyObject={{adServerId: params.id}} organization={selectedOrganization}/>
                    </Grid>
                </>
            </Grid>
        </>)
}


export default connector(AdServerOverview);
